import { useEffect, useMemo, useState } from "react";
import { createReceipt, formatMxn } from "../../shared/metrics";
import type { ParticipantReceipt, ServiceDefinition, SessionPublic } from "../../shared/model";
import { clientApi, getParticipantToken, loadReceipt, saveReceipt } from "../api";
import { Brand, StatusBadge } from "../components/Brand";
import { CommunityFooter } from "../components/CommunityFooter";
import { ErrorCard, Loading } from "../components/Loading";
import { ServiceLogo } from "../components/ServiceLogo";

function serviceDisplayName(service: ServiceDefinition): string {
  if (service.id === "max") return "HBO Max";
  if (service.id === "youtube-premium") return "YouTube Premium";
  return service.shortName;
}

function ServiceCard({
  service,
  selected,
  onToggle,
}: {
  service: ServiceDefinition;
  selected: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      aria-pressed={selected}
      className={`service-card ${selected ? "is-selected" : ""}`}
      onClick={onToggle}
      type="button"
    >
      <ServiceLogo glyph={service.glyph} serviceId={service.id} />
      <span className="service-card-copy">
        <strong>{serviceDisplayName(service)}</strong>
        <small>{selected ? "Seleccionada" : "Toca para elegir"}</small>
      </span>
      <span className="check-box" aria-hidden="true">
        {selected ? "✓" : ""}
      </span>
    </button>
  );
}

function PersonalResult({
  receipt,
  session,
  onEdit,
  pending,
  onRetry,
}: {
  receipt: ParticipantReceipt;
  session: SessionPublic;
  onEdit: () => void;
  pending: boolean;
  onRetry: () => void;
}) {
  const [horizon, setHorizon] = useState<1 | 6 | 12>(1);
  const [isTotalVisible, setIsTotalVisible] = useState(false);
  const selected = session.catalog.services.filter((service) =>
    receipt.selectedServiceIds.includes(service.id),
  );
  const total = receipt.monthlyTotalCents * horizon;
  const max = Math.max(...selected.map((service) => service.monthlyPriceCents), 1);

  return (
    <main className="participant-page result-page" id="main">
      <header className="participant-header">
        <Brand club href={`/s/${session.slug}`} />
        <StatusBadge tone={pending ? "yellow" : "green"}>
          {pending ? "Pendiente de enviar" : "Respuesta agregada"}
        </StatusBadge>
      </header>
      <section className="personal-breakdown section-card">
        <div className="section-heading">
          <span className="icon-box">↗</span>
          <div>
            <span className="eyebrow">TU MEZCLA DIGITAL</span>
            <h2>{selected.length} suscripciones</h2>
          </div>
        </div>
        {selected.length > 0 && (
          <p className="personal-breakdown-help">
            Cada barra compara el precio con tu servicio más caro.
          </p>
        )}
        {selected.length === 0 ? (
          <div className="zero-state">
            <strong>$0 también es un dato.</strong>
            <p>No marcaste suscripciones pagadas y tu respuesta sigue contando en la estadística.</p>
          </div>
        ) : (
          <div className="personal-bars">
            {selected
              .sort((a, b) => b.monthlyPriceCents - a.monthlyPriceCents)
              .map((service) => {
                const share =
                  receipt.monthlyTotalCents === 0
                    ? 0
                    : Math.round(
                        (service.monthlyPriceCents / receipt.monthlyTotalCents) * 100,
                      );
                const relativeWidth = (service.monthlyPriceCents / max) * 100;

                return (
                  <article
                    className={`personal-service-row ${
                      service.monthlyPriceCents === max ? "is-largest" : ""
                    }`}
                    key={service.id}
                  >
                    <div className="personal-service-summary">
                      <ServiceLogo glyph={service.glyph} serviceId={service.id} />
                      <span className="personal-service-copy">
                        <strong>{serviceDisplayName(service)}</strong>
                        <small>{service.plan}</small>
                      </span>
                      <span className="personal-service-price">
                        <b>{formatMxn(service.monthlyPriceCents * horizon)}</b>
                        <small>{share}% del total</small>
                      </span>
                    </div>
                    <div
                      aria-label={`${serviceDisplayName(service)} equivale al ${Math.round(
                        relativeWidth,
                      )}% del servicio más caro`}
                      className="personal-bar-track"
                      role="img"
                    >
                      <span style={{ width: `${relativeWidth}%` }} />
                    </div>
                  </article>
                );
              })}
          </div>
        )}
      </section>
      {pending && (
        <ErrorCard
          message="Tu cálculo está listo, pero todavía no se sumó a la pantalla del auditorio."
          onRetry={onRetry}
        />
      )}
      <section className="method-card">
        <strong>¿De dónde sale la cifra?</strong>
        <p>
          Usamos un plan representativo por servicio. Los precios pueden cambiar por promociones,
          impuestos, plataforma o modalidad familiar.
        </p>
      </section>
      <button className="hw-btn hw-btn--secondary edit-answer" onClick={onEdit} type="button">
        Editar mi selección
      </button>
      <section className={`result-hero result-hero--private ${isTotalVisible ? "is-visible" : ""}`}>
        <span className="eyebrow">TU RESULTADO PERSONAL</span>
        <div className="private-total">
          <h1 aria-live="polite">
            {isTotalVisible ? formatMxn(total) : "$•••"}
          </h1>
          <button
            aria-label={isTotalVisible ? "Ocultar mi gasto" : "Mostrar mi gasto"}
            aria-pressed={isTotalVisible}
            className="privacy-toggle"
            onClick={() => setIsTotalVisible((visible) => !visible)}
            type="button"
          >
            <svg aria-hidden="true" viewBox="0 0 32 32">
              <path d="M2.5 16S7.5 8 16 8s13.5 8 13.5 8-5 8-13.5 8S2.5 16 2.5 16Z" />
              <circle cx="16" cy="16" r="4.5" />
              {!isTotalVisible && <path className="privacy-toggle-slash" d="M5 4 27 28" />}
            </svg>
          </button>
        </div>
        <p>
          {horizon === 1 ? "al mes" : horizon === 6 ? "en seis meses" : "en un año"} · estimación
          con precios de referencia
        </p>
        <div className="segmented-control result-horizon">
          {[
            [1, "Mes"],
            [6, "6 meses"],
            [12, "Año"],
          ].map(([value, label]) => (
            <button
              className={horizon === value ? "is-active" : ""}
              key={value}
              onClick={() => setHorizon(value as 1 | 6 | 12)}
              type="button"
            >
              {label}
            </button>
          ))}
        </div>
        <small className="privacy-help">
          Toca el ojo para {isTotalVisible ? "ocultar" : "mostrar"} tu cifra.
        </small>
      </section>
      <CommunityFooter
        brandHref={`/s/${session.slug}`}
        keepCurrentView
      />
    </main>
  );
}

export function ParticipantPage({ slug }: { slug: string }) {
  const [session, setSession] = useState<SessionPublic | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [receipt, setReceipt] = useState<ParticipantReceipt | null>(() => loadReceipt(slug));
  const [editing, setEditing] = useState(!receipt);
  const [pending, setPending] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const loaded = await clientApi.getSession(slug);
        if (cancelled) return;
        setSession(loaded);
        const existing = loadReceipt(slug);
        if (existing) setSelected(existing.selectedServiceIds);
        setError(null);
      } catch (loadError) {
        if (!cancelled) setError(loadError instanceof Error ? loadError.message : "NETWORK_ERROR");
      }
    };
    void load();
    const timer = window.setInterval(() => {
      if (session?.status !== "open") void load();
    }, 3000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [session?.status, slug]);

  const offlineReceipt = useMemo(
    () => (session ? createReceipt("pending", selected, session.catalog) : null),
    [selected, session],
  );

  const submit = async () => {
    if (!session || !offlineReceipt) return;
    setSubmitting(true);
    setError(null);
    try {
      const saved = await clientApi.submit(slug, getParticipantToken(slug), selected);
      saveReceipt(slug, saved);
      setReceipt(saved);
      setPending(false);
      setEditing(false);
    } catch (submitError) {
      saveReceipt(slug, offlineReceipt);
      setReceipt(offlineReceipt);
      setPending(true);
      setEditing(false);
      setError(submitError instanceof Error ? submitError.message : "NETWORK_ERROR");
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (!pending) return;
    const retry = () => void submit();
    window.addEventListener("online", retry);
    return () => window.removeEventListener("online", retry);
  });

  if (!session && !error) return <Loading label="Preparando tu sesión…" />;
  if (!session) {
    return (
      <main className="center-page" id="main">
        <ErrorCard message="No encontramos esta sesión. Revisa el código o vuelve a escanear el QR." />
        <a className="hw-btn hw-btn--secondary" href="/">
          Volver al inicio
        </a>
      </main>
    );
  }
  if (receipt && !editing) {
    return (
      <PersonalResult
        onEdit={() => setEditing(true)}
        onRetry={() => void submit()}
        pending={pending}
        receipt={receipt}
        session={session}
      />
    );
  }

  return (
    <main className="participant-page" id="main">
      <header className="participant-header">
        <Brand club href={`/s/${slug}`} />
        <StatusBadge tone={session.status === "open" ? "green" : "yellow"}>
          {session.status === "open" ? "En vivo" : "Espera un momento"}
        </StatusBadge>
      </header>
      <section className="participant-intro">
        <h1>¿Qué suscripciones pagas cada mes?</h1>
        <p>
          Toca todas las que pagas tú o compartes. Verás el cálculo completo antes de enviarlo.
        </p>
        <div className="participant-hint">
          <span aria-hidden="true">✦</span>
          <span>
            Todo es anónimo. Tu selección se elimina 24 horas después de cerrar la sesión.
          </span>
        </div>
      </section>
      {session.status !== "open" ? (
        <div className="waiting-card">
          <span className="waiting-star">✦</span>
          <h2>La dinámica aún no comienza</h2>
          <p>Deja esta pantalla abierta. Se habilitará automáticamente cuando Sebs inicie.</p>
        </div>
      ) : (
        <>
          <section className="service-picker" aria-labelledby="service-picker-title">
            <div className="service-picker-heading">
              <div>
                <span className="eyebrow">TU MEZCLA DIGITAL</span>
                <h2 id="service-picker-title">Selecciona tus servicios</h2>
              </div>
              <span aria-live="polite">
                <strong>{selected.length}</strong>
                {selected.length === 1 ? " elegida" : " elegidas"}
              </span>
            </div>
            <div className="service-grid">
              {session.catalog.services.map((service) => (
                <ServiceCard
                  key={service.id}
                  onToggle={() =>
                    setSelected((current) =>
                      current.includes(service.id)
                        ? current.filter((id) => id !== service.id)
                        : [...current, service.id],
                    )
                  }
                  selected={selected.includes(service.id)}
                  service={service}
                />
              ))}
            </div>
          </section>
          <div className="sticky-submit">
            <div className="sticky-submit-inner">
              <span className="selection-count" aria-live="polite">
                <strong>{selected.length}</strong>
                <small>{selected.length === 1 ? "elegida" : "elegidas"}</small>
              </span>
              <button
                className="hw-btn hw-btn--primary"
                disabled={submitting}
                onClick={() => void submit()}
                type="button"
              >
                {submitting
                  ? "Calculando…"
                  : receipt
                    ? "Actualizar resultado →"
                    : selected.length === 0
                      ? "Continuar con $0 →"
                      : "Calcular mi gasto →"}
              </button>
            </div>
          </div>
        </>
      )}
    </main>
  );
}
